import React, { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { CashIcon, PlusIcon } from "@heroicons/react/outline";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Table from "../../../../Components/v2/Table";
import {
  addLocalNotification,
  clearBusinessTax,
  clearBusinessTaxError,
  fetchBusinessTax,
  requestDeleteBusinessTax,
  requestListBusinessTax,
  useAuthState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useNotificationDispatch,
} from "../../../../Context";
import Pagination from "../../../../Components/v2/Pagination";
import { useTranslation } from "react-i18next";
import TaxModal from "../../../../Components/v2/Accounting/TaxModal";
import TaxDetailModal from "../../../../Components/v2/Accounting/TaxDetailModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import { cancelAPIRequest, currencyFormat } from "../../../../Utils";
import { TAX_KIND_LABEL } from "../../../../Constants";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingTaxes = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(location.state?.tax ?? null);

  useEffect(() => {
    if (location.state?.tax) {
      setDetailModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reports link here with ?taxId= so the user can fix a tax's type. The
  // parameter is dropped once read, so closing the detail does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedTaxId = searchParams.get("taxId");
  // Not the effect's own cleanup: dropping the parameter re-runs the effect,
  // and that must not discard the tax still being fetched.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!linkedTaxId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("taxId");
    setSearchParams(next, { replace: true });
    fetchBusinessTax(linkedTaxId)
      .then((tax) => {
        if (!mounted.current || !tax) return;
        setSelectedTax(tax);
        setDetailModalOpen(true);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedTaxId]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const businessTaxDispatch = useBusinessTaxDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user } = useAuthState();
  const {
    request,
    businessTaxes,
    businessTaxesLoading,
    businessTaxNextTokens,
    businessTaxesError,
    deleteTaxLoading,
    deleteTaxError,
    deletedID,
  } = useBusinessTaxState();

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearBusinessTax(businessTaxDispatch);
      clearBusinessTaxError(businessTaxDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      setPage(1);
      requestListBusinessTax(businessTaxDispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
    }
  }, [businessTaxDispatch, perPage, user?.mainBusiness?.id]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-tax-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "taxes.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  useEffect(() => {
    if (deleteTaxError) {
      setShowConfirmModal(false);
    }
  }, [deleteTaxError]);

  useEffect(() => {
    if (deleteTaxError && !showConfirmModal) {
      setShowErrorModal(true);
    }
  }, [deleteTaxError, showConfirmModal]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (businessTaxNextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessTax(businessTaxDispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user?.mainBusiness?.id,
        nextTokens: businessTaxNextTokens[selectedPage],
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  const handleAdd = () => {
    setSelectedTax(null);
    setTaxModalOpen(true);
  };

  const handleViewDetail = (tax) => {
    setSelectedTax(tax);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTax(null);
  };

  const handleEditFromDetail = (tax) => {
    setDetailModalOpen(false);
    setSelectedTax(tax);
    setTaxModalOpen(true);
  };

  const handleEdit = (tax) => {
    setSelectedTax(tax);
    setTaxModalOpen(true);
  };

  const handleDelete = (tax) => {
    setSelectedTax(tax);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    requestDeleteBusinessTax(businessTaxDispatch, {
      id: selectedTax?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedTax) return null;
    const rate =
      selectedTax.computation === "PERCENTAGE"
        ? `${selectedTax.amount}%`
        : currencyFormat(selectedTax.amount);
    const date = dayjs(selectedTax.createdAt)
      .locale(i18n.language)
      .format("D MMM YYYY");
    return t("taxes.modal.description.deleteItem", {
      name: selectedTax.name,
      rate,
      date,
      interpolation: { escapeValue: false },
    });
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    setTaxModalOpen(false);
    setSelectedTax(null);
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {t("taxes.label")}
              </h2>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("taxes.header.title.add")}
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Table
            isLoading={businessTaxesLoading}
            error={businessTaxesError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "taxes.fields.name",
                sortable: true,
                sortKey: "name",
                accessor: (tax) => (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <CashIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {tax.name}
                        </div>
                        {tax.withholding && (
                          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            {t("taxes.badges.withheld")}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {tax.type === "SALES"
                          ? t("taxes.types.sales")
                          : t("taxes.types.purchases")}
                        {tax.kind &&
                          ` · ${t(
                            TAX_KIND_LABEL[tax.kind] || "taxes.kinds.other",
                          )}`}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                header: "taxes.fields.rate",
                accessor: (tax) => (
                  <div>
                    <div className="text-sm text-gray-900">
                      {tax.computation === "PERCENTAGE"
                        ? `${tax.amount}%`
                        : `Rp${tax.amount.toLocaleString()}`}
                    </div>
                    <div className="text-sm text-gray-500">
                      {tax.computation === "PERCENTAGE"
                        ? t("taxes.computations.percentage")
                        : t("taxes.computations.fixed")}
                    </div>
                  </div>
                ),
              },
              {
                header: "taxes.fields.taxAccount",
                accessor: (tax) => (
                  <div>
                    <div className="text-sm text-gray-900">
                      {tax.account?.name || "-"}
                    </div>
                    <div className="text-sm text-gray-500">
                      {tax.account?.code || "-"}
                    </div>
                  </div>
                ),
              },
              {
                header: "common.actions.label",
                accessor: (tax, index) => (
                  <TableActions
                    position={
                      index === businessTaxes.length - 1 ? "top" : "bottom"
                    }
                    actions={[
                      {
                        label: t("common.view"),
                        onClick: () => handleViewDetail(tax),
                      },
                      {
                        label: t("common.edit"),
                        onClick: () => handleEdit(tax),
                      },
                      {
                        label: t("common.delete"),
                        onClick: () => handleDelete(tax),
                        variant: "danger",
                        disabled: Boolean(tax.inUse),
                        tooltip: tax.inUse
                          ? t("common.inUseInTransactions")
                          : undefined,
                      },
                    ]}
                  />
                ),
                align: "right",
              },
            ]}
            data={businessTaxes}
          />
        </div>
        <Pagination
          nextTokens={businessTaxNextTokens}
          currentPage={page}
          isLoading={businessTaxesLoading}
          onPageChange={onPageChange}
          perPage={perPage}
          onPerPageChange={onPerPageChange}
        />
        {taxModalOpen && (
          <TaxModal
            open={taxModalOpen}
            onClose={handleClose}
            tax={selectedTax}
          />
        )}

        {/* Detail Modal */}
        {detailModalOpen && (
          <TaxDetailModal
            open={detailModalOpen}
            onClose={handleCloseDetailModal}
            tax={selectedTax}
            onEdit={handleEditFromDetail}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="taxes.modal.title.delete"
          description="taxes.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          loadingButton={deleteTaxLoading}
          disabledButton={deleteTaxLoading}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
          }}
        />

        {/* Error Modal */}
        {deleteTaxError && (
          <ErrorDialog
            open={showErrorModal}
            error={deleteTaxError}
            onClose={() => {
              setShowErrorModal(false);
              clearBusinessTaxError(businessTaxDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingTaxes;
